"use client";

import { useState, useMemo, useEffect, ReactNode, ChangeEvent } from "react";
import { useRouter } from "next/navigation";
import { useDataContext } from "@/contexts/DataContext";
import { PoolRiskApiResponseObject, Pair, Token } from "@/utils/types";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PERIOD_OPTIONS, AllowedPeriods, formatPeriodDisplay } from "@/utils/utilities";

interface PoolsTableProps {
  data: PoolRiskApiResponseObject[];
  pairs: Pair[];
  tokens: Token[];
}

type SortKey = keyof PoolRiskApiResponseObject;
type SortDirection = "asc" | "desc" | null;

interface SortConfig {
  key: SortKey | null;
  direction: SortDirection;
}

const PROTOCOLS = ["all", "soroswap", "aquarius", "phoenix", "comet", "sdex"] as const;
type ProtocolFilter = (typeof PROTOCOLS)[number];

export function PoolsTable({ data, pairs, tokens }: PoolsTableProps) {
  const dataContext = useDataContext();
  const { period, setPeriod } = dataContext;
  const buildPoolRoute = dataContext.buildPoolRoute;
  const router = useRouter();

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });
  const [protocolFilter, setProtocolFilter] = useState<ProtocolFilter>("all");

  const filteredData = useMemo(() => {
    return data.filter((pool) => {
      if (searchQuery !== "") {
        const lowerQuery = searchQuery.toLowerCase();
        if (
          !pool.market.toLowerCase().includes(lowerQuery) &&
          !pool.protocol.toLowerCase().includes(lowerQuery)
        ) {
          return false;
        }
      }

      if (protocolFilter !== "all") {
        const mappedProtocol = protocolFilter === "aquarius" ? "aqua" : protocolFilter;
        if (pool.protocol.toLowerCase() !== mappedProtocol.toLowerCase()) {
          return false;
        }
      }

      return true;
    });
  }, [data, searchQuery, protocolFilter]);

  const handleSort = (key: SortKey) => {
    setSortConfig((current) => {
      if (current.key === key) {
        if (current.direction === "asc") {
          return { key, direction: "desc" };
        }
        if (current.direction === "desc") {
          return { key: null, direction: null };
        }
      }
      return { key, direction: "asc" };
    });
  };

  const sortedData = useMemo(() => {
    const d = [...filteredData];
    const key = sortConfig.key;
    const dir = sortConfig.direction;

    if (!key || !dir) return d;

    const getVal = (row: PoolRiskApiResponseObject): number => {
      const val = row[key] as unknown;
      if (typeof val === "number") return val;
      if (typeof val === "string") {
        const parsed = parseFloat(val.replace(/[^0-9.-]+/g, ""));
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    };

    const sign = dir === "asc" ? 1 : -1;
    d.sort((a, b) => (getVal(a) - getVal(b)) * sign);
    return d;
  }, [filteredData, sortConfig.key, sortConfig.direction]);

  const totalPages = Math.ceil(sortedData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + entriesPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, protocolFilter, entriesPerPage]);

  const getProtocolDisplay = (proto: string) => {
    const lower = proto.toLowerCase();
    if (lower === "aqua") return "Aquarius";
    if (lower === "sdex") return "SDEX";
    if (lower === "comet") return "Comet";
    return proto.charAt(0).toUpperCase() + proto.slice(1);
  };

  const onRowClick = (pool: PoolRiskApiResponseObject) => {
    const route = buildPoolRoute(pool);
    router.push(route);
  };

  const resetFilters = () => {
    setSearchQuery("");
    setProtocolFilter("all");
    setCurrentPage(1);
  };

  const SortableHeader = ({
    children,
    sortKey,
    align = "left",
  }: {
    children: ReactNode;
    sortKey: SortKey;
    align?: "left" | "right";
  }) => (
    <TableHead
      onClick={() => { handleSort(sortKey); }}
      className={cn("cursor-pointer select-none", align === "right" ? "text-right" : "text-left")}
    >
      <div
        className="flex items-center gap-1"
        style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
      >
        <span>{children}</span>
        <ArrowUpDown
          className={`h-3 w-3 ${sortConfig.key === sortKey ? "text-foreground" : "text-muted-foreground/50"}`}
        />
      </div>
    </TableHead>
  );

  return (
    <div>
      {/* Filter bar */}
      <div className="filter-bar">
        <div className="filter-pills">
          {PROTOCOLS.map((proto) => (
            <button
              key={proto}
              className={`pill${protocolFilter === proto ? " pill-active" : ""}`}
              onClick={() => { setProtocolFilter(proto); }}
            >
              {proto === "all"
                ? "All"
                : proto === "aquarius"
                  ? "Aquarius"
                  : proto === "sdex"
                    ? "SDEX"
                    : proto.charAt(0).toUpperCase() + proto.slice(1)}
            </button>
          ))}
        </div>
        <div className="search-wrap">
          <svg className="search-icon-pos" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="search-input"
            placeholder="Search by token / pair / pool address"
            value={searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>) => { setSearchQuery(e.target.value); }}
            aria-label="Search pools"
          />
        </div>
        <button className="btn-sm" onClick={resetFilters} aria-label="Reset filters">RESET</button>
        <Select
          value={period}
          onValueChange={(v) => { setPeriod(v as AllowedPeriods); }}
          aria-label="Select time period"
        >
          <SelectTrigger
            className="h-auto w-auto px-[10px] py-[5px] text-[10px] tracking-[0.06em] rounded-[var(--r)] shadow-none"
            style={{ background: "var(--surface-hi)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}
          >
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label} Period
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="table-header-label">Protocol</TableHead>
              <TableHead className="table-header-label">Pair/Pool</TableHead>
              <SortableHeader sortKey="apr">Est. {formatPeriodDisplay(period)} APR</SortableHeader>
              <SortableHeader sortKey="totalValueLocked">TVL</SortableHeader>
              <SortableHeader sortKey="volume">{formatPeriodDisplay(period)} Volume</SortableHeader>
              <SortableHeader sortKey="fees">{formatPeriodDisplay(period)} Fees</SortableHeader>
              <SortableHeader sortKey="riskScore">Risk Score</SortableHeader>
              <TableHead className="table-header-label">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="table-cell-base">
                  No pools data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((pool, index) => (
                <TableRow
                  key={index}
                  className="group table-row-hover"
                  onClick={() => { onRowClick(pool); }}
                >
                  <TableCell>
                    <Badge
                      variant={
                        pool.protocol === "soroswap"
                          ? "soroswap"
                          : pool.protocol === "blend"
                            ? "blend"
                            : pool.protocol === "phoenix"
                              ? "phoenix"
                              : pool.protocol === "aqua"
                                ? "aqua"
                                : pool.protocol === "comet"
                                  ? "comet"
                                  : pool.protocol === "sdex"
                                    ? "sdex"
                                    : "outline"
                      }
                      className="capitalize"
                    >
                      {getProtocolDisplay(pool.protocol)}
                    </Badge>
                  </TableCell>
                  <TableCell>{pool.market}</TableCell>
                  <TableCell style={{ color: parseFloat(pool.apr) > 0 ? "var(--green)" : "var(--text-secondary)" }}>{pool.apr}</TableCell>
                  <TableCell style={{ color: "var(--text-secondary)" }}>
                    ${Number(pool.totalValueLocked).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell style={{ color: "var(--text-secondary)" }}>
                    ${Number(pool.volume).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell style={{ color: "var(--text-secondary)" }}>
                    ${Number(pool.fees).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    <span style={{ color: Number(pool.riskScore) <= 50 ? "var(--green)" : "var(--red)" }}>
                      {Number(pool.riskScore).toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center gap-1 text-[11px] cursor-pointer transition-colors"
                      style={{ color: "var(--text-secondary)" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(pool);
                      }}
                    >
                      View Details
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {sortedData.length > entriesPerPage && (
          <div className="table-footer">
            <div className="table-footer-left">
              <div className="table-footer-entries">
                <span>Show</span>
                <Select
                  value={entriesPerPage.toString()}
                  onValueChange={(value) => {
                    setEntriesPerPage(parseInt(value, 10));
                    setCurrentPage(1);
                  }}
                  aria-label="Number of entries per page"
                >
                  <SelectTrigger
                    className="h-auto w-auto px-[8px] py-[3px] text-[10px] rounded-[var(--r)] shadow-none"
                    style={{ background: "var(--surface-hi)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}
                  >
                    <SelectValue placeholder="10" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>entries</span>
              </div>
              <span>
                Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, sortedData.length)} of {sortedData.length} entries
              </span>
            </div>
            <div className="table-footer-right">
              <button
                className="btn-sm"
                onClick={() => { setCurrentPage((prev) => Math.max(1, prev - 1)); }}
                disabled={currentPage === 1}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, index, array) => (
                  <span key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span style={{ padding: "0 4px", color: "var(--text-tertiary)" }}>...</span>
                    )}
                    <button
                      className={`page-num${currentPage === page ? " page-num-active" : ""}`}
                      onClick={() => { setCurrentPage(page); }}
                    >
                      {page}
                    </button>
                  </span>
                ))}
              <button
                className="btn-sm"
                onClick={() => { setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                disabled={currentPage === totalPages}
              >
                Next
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
